import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

/**
 * Simple policy page shell. Embeds the PDF served by the API at
 * /api/legal/<slug>.pdf and offers a Download button. All four public
 * policies (Terms, Privacy, Refund, Acceptable Use) reuse this shell.
 *
 * We deliberately avoid re-typesetting the policy text here — the
 * canonical documents are the PDFs supplied by the business owner.
 */
export default function LegalPage({ title, slug, testIdPrefix, children }) {
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/legal/${slug}.pdf`;
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-rose-400"
            data-testid={`${testIdPrefix}-home-link`}
          >
            ← My Panda X
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Adults 18+ only
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4"
          data-testid={`${testIdPrefix}-heading`}
        >
          {title}
        </h1>
        {children}
        <p className="text-sm text-zinc-500 mb-8">
          The document below is the authoritative version. If your browser
          can't display the embedded PDF, use the Download button.
        </p>

        <div className="flex flex-wrap gap-3 mb-6">
          <a
            href={pdfUrl}
            download
            data-testid={`${testIdPrefix}-download`}
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white text-xs uppercase tracking-widest px-4 py-2 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download PDF
          </a>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testIdPrefix}-open`}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-rose-500 text-zinc-300 text-xs uppercase tracking-widest px-4 py-2 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
          </a>
        </div>

        <div
          className="border border-white/10 bg-white/[0.02]"
          data-testid={`${testIdPrefix}-viewer`}
        >
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-full h-[80vh] bg-white"
            aria-label={title}
          >
            <div className="p-6 text-sm text-zinc-400">
              Your browser can't preview PDFs.{" "}
              <a
                href={pdfUrl}
                className="text-rose-400 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the document
              </a>{" "}
              in a new tab.
            </div>
          </object>
        </div>

        <p className="mt-8 text-xs text-zinc-500">
          Questions? Email{" "}
          <a
            href="mailto:support@mypandax.com"
            className="text-rose-400 hover:underline"
          >
            support@mypandax.com
          </a>
          .
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}
